import { useCallback, useEffect } from "react";

interface UseAnnotationNavigationParams {
  enabled: boolean; // Only enabled when NOT annotating
  currentIndex: number;
  totalImages: number;
  onNavigate: (index: number) => void;
}

interface KeyboardShortcut {
  key: string;
  ctrlKey?: boolean;
  metaKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
  handler: (event: KeyboardEvent) => void;
  preventDefault?: boolean;
}

interface UseKeyboardShortcutsOptions {
  shortcuts: KeyboardShortcut[];
  enabled?: boolean;
  target?: HTMLElement | Window;
}

/**
 * Custom hook for centralized keyboard shortcut handling
 * Manages event listeners and cleanup automatically
 */
export function useKeyboardShortcuts({
  shortcuts,
  enabled = true,
  target = window,
}: UseKeyboardShortcutsOptions): void {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled) return;

      for (const shortcut of shortcuts) {
        const matchesKey = event.key === shortcut.key;
        const matchesCtrl = shortcut.ctrlKey === undefined || event.ctrlKey === shortcut.ctrlKey;
        const matchesMeta = shortcut.metaKey === undefined || event.metaKey === shortcut.metaKey;
        const matchesShift = shortcut.shiftKey === undefined || event.shiftKey === shortcut.shiftKey;
        const matchesAlt = shortcut.altKey === undefined || event.altKey === shortcut.altKey;

        if (matchesKey && matchesCtrl && matchesMeta && matchesShift && matchesAlt) {
          if (shortcut.preventDefault !== false) {
            event.preventDefault();
          }
          shortcut.handler(event);
          break; // Only handle the first matching shortcut
        }
      }
    },
    [shortcuts, enabled],
  );

  useEffect(() => {
    if (!enabled) return;

    const targetElement = target instanceof Window ? window : target;
    targetElement.addEventListener("keydown", handleKeyDown as EventListener);

    return () => {
      targetElement.removeEventListener("keydown", handleKeyDown as EventListener);
    };
  }, [handleKeyDown, enabled, target]);
}

/**
 * Simplified keyboard navigation hook for AnnotationMediaViewer
 * Uses the existing keyboard shortcut infrastructure for consistency
 */
export function useAnnotationNavigation({
  enabled,
  currentIndex,
  totalImages,
  onNavigate,
}: UseAnnotationNavigationParams): void {
  // Handle left arrow navigation
  const handleNavigateLeft = useCallback(() => {
    const newIndex = currentIndex > 0 ? currentIndex - 1 : totalImages - 1;
    onNavigate(newIndex);
  }, [currentIndex, totalImages, onNavigate]);

  // Handle right arrow navigation
  const handleNavigateRight = useCallback(() => {
    const newIndex = currentIndex < totalImages - 1 ? currentIndex + 1 : 0;
    onNavigate(newIndex);
  }, [currentIndex, totalImages, onNavigate]);

  // Create arrow shortcuts
  const shortcuts = createArrowShortcuts({
    left: handleNavigateLeft,
    right: handleNavigateRight,
  });

  // Use the keyboard shortcuts hook
  useKeyboardShortcuts({
    shortcuts,
    enabled: enabled && totalImages > 1,
  });
}

function createArrowShortcuts(handlers: {
  left?: () => void;
  right?: () => void;
  up?: () => void;
  down?: () => void;
}): KeyboardShortcut[] {
  const shortcuts: KeyboardShortcut[] = [];

  if (handlers.left) {
    shortcuts.push(createShortcut("ArrowLeft", handlers.left));
  }
  if (handlers.right) {
    shortcuts.push(createShortcut("ArrowRight", handlers.right));
  }
  if (handlers.up) {
    shortcuts.push(createShortcut("ArrowUp", handlers.up));
  }
  if (handlers.down) {
    shortcuts.push(createShortcut("ArrowDown", handlers.down));
  }

  return shortcuts;
}

// Convenience functions for common modifier combinations
export function createShortcut(
  key: string,
  handler: () => void,
  modifiers: Partial<Omit<KeyboardShortcut, "key" | "handler">> = {},
): KeyboardShortcut {
  return {
    key,
    handler: () => handler(),
    preventDefault: true,
    ...modifiers,
  };
}
